import { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import axiosInstance from '../../../axios/axiosInstance';
import apiEndpoints from '../../../axios/api';
import usePermissions from '../../../hook/usePermissions';
import { PERMISSIONS } from '../../../consts/permissions';
import { usePageRefresh } from '../../../context/PageRefreshContext';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import { localDateString } from '../../../utils/helpers/localDate';

// Daily to-do list for employees. Items recur every day (or on the chosen
// weekdays) for all employees or the selected ones; each employee marks them
// Done / In progress / Not done on punch-out (Punch Attendance and the app),
// and the answers show here per day and in the work log.

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const STATUS_BADGE = {
    COMPLETED: { label: 'Done', cls: 'tw-bg-emerald-50 tw-text-emerald-700' },
    IN_PROGRESS: { label: 'In progress', cls: 'tw-bg-blue-50 tw-text-blue-700' },
    PENDING: { label: 'Not done', cls: 'tw-bg-amber-50 tw-text-amber-700' },
};
const inputClass = 'tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-400';
const EMPTY = { title: '', description: '', applies_to: 'ALL', employee_ids: [], weekdays: [], is_active: true };

const employeeName = (e) => [e.first_name, e.last_name].filter(Boolean).join(' ') || e.username || `Employee #${e.employee_id}`;

const ItemModal = ({ item, employees, onClose, onSaved }) => {
    const [form, setForm] = useState(item
        ? { ...EMPTY, ...item, weekdays: item.weekdays || [], employee_ids: item.employee_ids || [] }
        : EMPTY);
    const [search, setSearch] = useState('');
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
    const toggleIn = (k, v) => set(k, form[k].includes(v) ? form[k].filter(x => x !== v) : [...form[k], v]);

    const shown = employees.filter(e => employeeName(e).toLowerCase().includes(search.trim().toLowerCase()));

    const save = async () => {
        if (!form.title.trim()) { toast.error('Enter a title'); return; }
        if (form.applies_to === 'SELECTED' && !form.employee_ids.length) { toast.error('Select at least one employee'); return; }
        setSaving(true);
        const body = {
            title: form.title.trim(),
            description: form.description?.trim() || '',
            applies_to: form.applies_to,
            employee_ids: form.applies_to === 'SELECTED' ? form.employee_ids : [],
            weekdays: form.weekdays.length ? form.weekdays : null,
            is_active: !!form.is_active,
        };
        try {
            if (item) await axiosInstance.put(apiEndpoints.DAILY_TODO_DETAIL_API.replace(':id', item.todo_id), body);
            else await axiosInstance.post(apiEndpoints.DAILY_TODOS_API, body);
            toast.success(item ? 'To-do item updated' : 'To-do item added');
            onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'Could not save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-z-50 tw-flex tw-items-center tw-justify-center tw-p-4" onClick={onClose}>
            <div className="tw-bg-white tw-rounded-xl tw-w-full tw-max-w-lg tw-max-h-[90vh] tw-flex tw-flex-col tw-shadow-xl" onClick={e => e.stopPropagation()}>
                <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-border-b tw-border-gray-100">
                    <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-m-0">{item ? 'Edit to-do item' : 'Add to-do item'}</h3>
                    <button onClick={onClose} className="tw-p-1 tw-rounded-full hover:tw-bg-gray-100"><Icon icon="mdi:close" className="tw-text-base tw-text-gray-400" /></button>
                </div>
                <div className="tw-p-5 tw-space-y-4 tw-overflow-y-auto">
                    <div>
                        <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-1">Title <span className="tw-text-rose-500">*</span></label>
                        <input value={form.title} maxLength={200} onChange={e => set('title', e.target.value)} placeholder="e.g. Check water tank level" className={inputClass} />
                    </div>
                    <div>
                        <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-1">Details</label>
                        <textarea rows={2} value={form.description || ''} maxLength={2000} onChange={e => set('description', e.target.value)} placeholder="Optional instructions" className={inputClass} />
                    </div>
                    <div>
                        <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-1">Repeats on</label>
                        <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                            {WEEKDAYS.map((d, i) => (
                                <button key={d} type="button" onClick={() => toggleIn('weekdays', i)}
                                    className={`tw-px-2.5 tw-py-1 tw-rounded-full tw-text-xs tw-border ${form.weekdays.includes(i) ? 'tw-bg-rose-600 tw-border-rose-600 tw-text-white' : 'tw-bg-white tw-border-gray-200 tw-text-gray-600'}`}>
                                    {d}
                                </button>
                            ))}
                        </div>
                        <p className="tw-text-[11px] tw-text-gray-400 tw-mt-1 tw-mb-0">{form.weekdays.length && form.weekdays.length < 7 ? 'Only on the selected days' : 'Every day - pick days to limit it'}</p>
                    </div>
                    <div>
                        <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-700 tw-mb-1">For</label>
                        <div className="tw-flex tw-gap-4 tw-text-sm">
                            {[['ALL', 'All employees'], ['SELECTED', 'Selected employees']].map(([v, l]) => (
                                <label key={v} className="tw-flex tw-items-center tw-gap-1.5 tw-cursor-pointer">
                                    <input type="radio" checked={form.applies_to === v} onChange={() => set('applies_to', v)} /> {l}
                                </label>
                            ))}
                        </div>
                        {form.applies_to === 'SELECTED' && (
                            <div className="tw-mt-2 tw-border tw-border-gray-200 tw-rounded-md">
                                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search employees" className="tw-w-full tw-px-3 tw-py-1.5 tw-text-xs tw-border-0 tw-border-b tw-border-gray-100 focus:tw-outline-none" />
                                <div className="tw-max-h-44 tw-overflow-y-auto tw-p-2 tw-space-y-1">
                                    {shown.map(e => (
                                        <label key={e.employee_id} className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-gray-700 tw-cursor-pointer">
                                            <input type="checkbox" checked={form.employee_ids.includes(Number(e.employee_id))} onChange={() => toggleIn('employee_ids', Number(e.employee_id))} />
                                            {employeeName(e)}{e.employee_type ? <span className="tw-text-gray-400"> · {e.employee_type}</span> : null}
                                        </label>
                                    ))}
                                    {shown.length === 0 && <p className="tw-text-[11px] tw-text-gray-400 tw-m-0">No employees found.</p>}
                                </div>
                                <p className="tw-text-[11px] tw-text-gray-500 tw-px-3 tw-py-1.5 tw-border-t tw-border-gray-100 tw-m-0">{form.employee_ids.length} selected</p>
                            </div>
                        )}
                    </div>
                    <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-700 tw-cursor-pointer">
                        <input type="checkbox" checked={!!form.is_active} onChange={e => set('is_active', e.target.checked)} /> Active
                    </label>
                </div>
                <div className="tw-flex tw-justify-end tw-gap-2 tw-px-5 tw-py-3 tw-border-t tw-border-gray-100">
                    <button onClick={onClose} className="tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50">Cancel</button>
                    <button onClick={save} disabled={saving} className="tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium tw-text-white tw-bg-rose-600 tw-rounded-md hover:tw-bg-rose-700 disabled:tw-opacity-50">{saving ? 'Saving…' : 'Save'}</button>
                </div>
            </div>
        </div>
    );
};

const DailyTodos = () => {
    const societyId = selectedSocietyStorer.getSocietyId();
    const { can } = usePermissions();
    const canEdit = can(PERMISSIONS.EMPLOYEE_UPDATE);
    const [tab, setTab] = useState('items');
    const [items, setItems] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null); // null | 'new' | item
    const [date, setDate] = useState(localDateString());
    const [dayStatus, setDayStatus] = useState(null);
    const [statusLoading, setStatusLoading] = useState(false);
    const [expanded, setExpanded] = useState(null);

    const loadItems = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get(apiEndpoints.DAILY_TODOS_API);
            setItems(Array.isArray(res?.data) ? res.data : []);
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'Failed to load to-do items');
        } finally {
            setLoading(false);
        }
    }, []);

    const loadStatus = useCallback(async () => {
        setStatusLoading(true);
        try {
            const res = await axiosInstance.get(apiEndpoints.DAILY_TODO_STATUS_API, { params: { date } });
            setDayStatus(res?.data || null);
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'Failed to load the day');
        } finally {
            setStatusLoading(false);
        }
    }, [date]);

    useEffect(() => { loadItems(); }, [loadItems]);
    useEffect(() => { if (tab === 'status') loadStatus(); }, [tab, loadStatus]);
    useEffect(() => {
        axiosInstance.get(apiEndpoints.SOCIETY_GET_EMPLOYEES_DROPDOWN_API, { params: { society_id: societyId } })
            .then(res => setEmployees(Array.isArray(res?.data) ? res.data : []))
            .catch(() => setEmployees([]));
    }, [societyId]);
    usePageRefresh(() => (tab === 'status' ? loadStatus() : loadItems()));

    const employeeById = useMemo(() => new Map(employees.map(e => [Number(e.employee_id), e])), [employees]);

    const toggleActive = async (item) => {
        try {
            await axiosInstance.put(apiEndpoints.DAILY_TODO_DETAIL_API.replace(':id', item.todo_id), { is_active: !item.is_active });
            loadItems();
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'Could not update');
        }
    };
    const remove = async (item) => {
        if (!window.confirm(`Remove "${item.title}"? Past answers are kept.`)) return;
        try {
            await axiosInstance.delete(apiEndpoints.DAILY_TODO_DETAIL_API.replace(':id', item.todo_id));
            toast.success('To-do item removed');
            loadItems();
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'Could not remove');
        }
    };

    const forLabel = (item) => (item.applies_to === 'ALL'
        ? 'All employees'
        : item.employee_ids.map(id => (employeeById.get(id) ? employeeName(employeeById.get(id)) : `#${id}`)).join(', '));

    return (
        <div className="tw-p-5">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-mb-4">
                <div>
                    <h1 className="tw-text-base tw-font-bold tw-text-gray-800 tw-m-0">Daily To-Do</h1>
                    <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5 tw-mb-0">Daily checklist employees mark Done / In progress / Not done when they punch out</p>
                </div>
                {canEdit && tab === 'items' && (
                    <button onClick={() => setEditing('new')} className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700">
                        <Icon icon="mdi:plus" className="tw-text-base" /> Add to-do item
                    </button>
                )}
            </div>

            <div className="tw-inline-flex tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-1 tw-mb-4">
                {[['items', 'To-do items', 'mdi:format-list-checks'], ['status', 'Status by day', 'mdi:calendar-check-outline']].map(([k, l, ic]) => (
                    <button key={k} onClick={() => setTab(k)} className={`tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium ${tab === k ? 'tw-bg-rose-600 tw-text-white' : 'tw-text-gray-600 hover:tw-bg-gray-50'}`}>
                        <Icon icon={ic} /> {l}
                    </button>
                ))}
            </div>

            {tab === 'items' && (
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm">
                    {loading ? (
                        <div className="tw-flex tw-justify-center tw-py-12"><div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-rose-600"></div></div>
                    ) : items.length === 0 ? (
                        <div className="tw-text-center tw-py-12">
                            <Icon icon="mdi:format-list-checks" className="tw-text-4xl tw-text-gray-300 tw-mx-auto" />
                            <p className="tw-text-sm tw-text-gray-500 tw-mt-2">No to-do items yet.</p>
                        </div>
                    ) : (
                        <div className="tw-overflow-x-auto">
                            <table className="tw-w-full tw-text-xs">
                                <thead>
                                    <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                        {['To-do', 'Repeats', 'For', 'Status', ...(canEdit ? [''] : [])].map(h => (
                                            <th key={h || 'actions'} className="tw-px-3 tw-py-2.5 tw-text-left tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map(item => (
                                        <tr key={item.todo_id} className="tw-border-b tw-border-gray-50">
                                            <td className="tw-px-3 tw-py-2.5">
                                                <div className="tw-font-medium tw-text-gray-800">{item.title}</div>
                                                {item.description && <div className="tw-text-[10px] tw-text-gray-400">{item.description}</div>}
                                            </td>
                                            <td className="tw-px-3 tw-py-2.5 tw-text-gray-600 tw-whitespace-nowrap">{item.weekdays ? item.weekdays.map(d => WEEKDAYS[d]).join(', ') : 'Every day'}</td>
                                            <td className="tw-px-3 tw-py-2.5 tw-text-gray-600 tw-max-w-[260px] tw-truncate" title={forLabel(item)}>{forLabel(item)}</td>
                                            <td className="tw-px-3 tw-py-2.5">
                                                <span className={`tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-medium ${item.is_active ? 'tw-bg-emerald-50 tw-text-emerald-700' : 'tw-bg-gray-100 tw-text-gray-500'}`}>{item.is_active ? 'Active' : 'Inactive'}</span>
                                            </td>
                                            {canEdit && (
                                                <td className="tw-px-3 tw-py-2.5 tw-text-right tw-whitespace-nowrap">
                                                    <button onClick={() => setEditing(item)} title="Edit" className="tw-p-1.5 tw-rounded hover:tw-bg-gray-100 tw-text-gray-600"><Icon icon="mdi:pencil-outline" className="tw-text-base" /></button>
                                                    <button onClick={() => toggleActive(item)} title={item.is_active ? 'Deactivate' : 'Activate'} className="tw-p-1.5 tw-rounded hover:tw-bg-gray-100 tw-text-gray-600"><Icon icon={item.is_active ? 'mdi:pause-circle-outline' : 'mdi:play-circle-outline'} className="tw-text-base" /></button>
                                                    <button onClick={() => remove(item)} title="Remove" className="tw-p-1.5 tw-rounded hover:tw-bg-rose-50 tw-text-rose-600"><Icon icon="mdi:trash-can-outline" className="tw-text-base" /></button>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {tab === 'status' && (
                <div className="tw-space-y-3">
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <label className="tw-text-xs tw-text-gray-600">Date</label>
                        <input type="date" value={date} max={localDateString()} onChange={e => setDate(e.target.value)} className="tw-px-2 tw-py-1.5 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md" />
                    </div>
                    {statusLoading ? (
                        <div className="tw-flex tw-justify-center tw-py-12"><div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-rose-600"></div></div>
                    ) : !dayStatus?.items?.length ? (
                        <p className="tw-text-sm tw-text-gray-500 tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-p-6 tw-text-center">No to-do items for this day.</p>
                    ) : dayStatus.items.map(it => (
                        <div key={it.todo_id} className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm">
                            <button onClick={() => setExpanded(expanded === it.todo_id ? null : it.todo_id)} className="tw-w-full tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-px-4 tw-py-3 tw-text-left">
                                <span className="tw-text-sm tw-font-medium tw-text-gray-800">{it.title}</span>
                                <span className="tw-flex tw-flex-wrap tw-gap-1.5 tw-text-[10px] tw-font-medium">
                                    <span className="tw-px-2 tw-py-0.5 tw-rounded-full tw-bg-emerald-50 tw-text-emerald-700">Done {it.counts.completed}</span>
                                    <span className="tw-px-2 tw-py-0.5 tw-rounded-full tw-bg-blue-50 tw-text-blue-700">In progress {it.counts.in_progress}</span>
                                    <span className="tw-px-2 tw-py-0.5 tw-rounded-full tw-bg-amber-50 tw-text-amber-700">Not done {it.counts.pending}</span>
                                    <span className="tw-px-2 tw-py-0.5 tw-rounded-full tw-bg-gray-100 tw-text-gray-500">Not marked {it.counts.not_answered}</span>
                                    <Icon icon={expanded === it.todo_id ? 'mdi:chevron-up' : 'mdi:chevron-down'} className="tw-text-base tw-text-gray-400" />
                                </span>
                            </button>
                            {expanded === it.todo_id && (
                                <div className="tw-border-t tw-border-gray-100 tw-divide-y tw-divide-gray-50">
                                    {it.employees.map(e => (
                                        <div key={e.employee_id} className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-4 tw-py-2 tw-text-xs">
                                            <div className="tw-min-w-0">
                                                <span className="tw-text-gray-800">{e.name}</span>
                                                {e.remark && <span className="tw-text-gray-400"> · {e.remark}</span>}
                                            </div>
                                            <span className={`tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-medium tw-whitespace-nowrap ${STATUS_BADGE[e.status]?.cls || 'tw-bg-gray-100 tw-text-gray-500'}`}>
                                                {STATUS_BADGE[e.status]?.label || 'Not marked'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {editing && (
                <ItemModal
                    item={editing === 'new' ? null : editing}
                    employees={employees}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); loadItems(); }}
                />
            )}
        </div>
    );
};

export default DailyTodos;
